import moment from 'moment';
import { ModelBase } from '../abstract/model-base.model';
import { Account } from '../accounting/account.model';
import { StatusEnum } from '../../enums/status.enum';
import { tagAccents } from 'src/assets/themes/biifor/components/tag';

export class Remuneration extends ModelBase {

    static override CLASS_NAME: string = 'Remuneration';

    private _label!: string;
    private _date!: moment.Moment;
    private _period!: moment.Moment;
    private _netAmount!: number;
    private _grossAmount!: number;
    private _employerSocialAmount!: number;
    private _totalSocialAmount!: number;
    private _taxWithholdingAmount!: number;
    private _beneficiaryType!: string;
    private _beneficiaryObjectId!: number;
    private _beneficiaryAccount!: Account;
    private _socialOrganismAccount!: Account;
    private _salaryTaxAccount!: Account;
    private _socialContributionAccount!: Account;
    private _salaryGrossAccount!: Account;

    // Méthodes statiques pour la gestion des statuts
    public static statusSeverity(status: string): 'success' | 'secondary' | 'primary' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (status) {
            case StatusEnum.Draft: return 'secondary';
            case StatusEnum.Validated: return 'primary';
            case StatusEnum.Paid: return 'success';
            default: return undefined;
        }
    }

    public static statusLabel(status: string): string {
        switch (status) {
            case StatusEnum.Draft: return 'Brouillon';
            case StatusEnum.Validated: return 'Validée';
            case StatusEnum.Paid: return 'Payée';
            default: return status;
        }
    }

    public static tagAccent(status: string): Object | undefined {
        switch (status) {
            case StatusEnum.Paid: return tagAccents;
            default: return undefined;
        }
    }

    public get statusSeverity(): 'success' | 'secondary' | 'primary' | 'warn' | 'danger' | 'contrast' | undefined {
        const s = this.status?.[0];
        return Remuneration.statusSeverity(s);
    }

    public get statusLabel(): string {
        const s = this.status?.[0];
        return Remuneration.statusLabel(s);
    }

    public get tagAccent(): Object | undefined {
        const s = this.status?.[0];
        return Remuneration.tagAccent(s);
    }

    public get isDraft(): boolean {
        return this.hasStatus(StatusEnum.Draft);
    }

    public get isValidated(): boolean {
        return this.hasStatus(StatusEnum.Validated);
    }

    public get label(): string {
        return this._label;
    }

    public set label(value: any) {
        this._setValue('_label', value, 'string');
    }

    public get date(): moment.Moment {
        return this._date;
    }

    public set date(value: any) {
        this._setValue('_date', value, 'date');
    }

    public get period(): moment.Moment {
        return this._period;
    }

    public set period(value: any) {
        this._setValue('_period', value, 'date');
    }

    public get netAmount(): number {
        return this._netAmount;
    }

    public set netAmount(value: any) {
        this._setValue('_netAmount', value, 'float');
    }

    public get grossAmount(): number {
        return this._grossAmount;
    }

    public set grossAmount(value: any) {
        this._setValue('_grossAmount', value, 'float');
    }

    public get employerSocialAmount(): number {
        return this._employerSocialAmount;
    }

    public set employerSocialAmount(value: any) {
        this._setValue('_employerSocialAmount', value, 'float');
    }

    public get totalSocialAmount(): number {
        return this._totalSocialAmount;
    }

    public set totalSocialAmount(value: any) {
        this._setValue('_totalSocialAmount', value, 'float');
    }

    public get taxWithholdingAmount(): number {
        return this._taxWithholdingAmount;
    }

    public set taxWithholdingAmount(value: any) {
        this._setValue('_taxWithholdingAmount', value, 'float');
    }

    public get beneficiaryType(): string {
        return this._beneficiaryType;
    }

    public set beneficiaryType(value: any) {
        this._setValue('_beneficiaryType', value, 'string');
    }

    public get beneficiaryObjectId(): number {
        return this._beneficiaryObjectId;
    }

    public set beneficiaryObjectId(value: any) {
        this._setValue('_beneficiaryObjectId', value, 'integer');
    }

    public get beneficiaryAccount(): Account {
        return this._beneficiaryAccount;
    }

    public set beneficiaryAccount(value: any) {
        this._setValue('_beneficiaryAccount', value, 'Account');
    }

    public get socialOrganismAccount(): Account {
        return this._socialOrganismAccount;
    }

    public set socialOrganismAccount(value: any) {
        this._setValue('_socialOrganismAccount', value, 'Account');
    }

    public get salaryTaxAccount(): Account {
        return this._salaryTaxAccount;
    }

    public set salaryTaxAccount(value: any) {
        this._setValue('_salaryTaxAccount', value, 'Account');
    }

    public get socialContributionAccount(): Account {
        return this._socialContributionAccount;
    }

    public set socialContributionAccount(value: any) {
        this._setValue('_socialContributionAccount', value, 'Account');
    }

    public get salaryGrossAccount(): Account {
        return this._salaryGrossAccount;
    }

    public set salaryGrossAccount(value: any) {
        this._setValue('_salaryGrossAccount', value, 'Account');
    }
}
